<div class="messenger">

    <!-- Ticket list (chat list) -->
    <aside class="card chat-list">
        <div class="chat-list-header">
            <span class="fs-3 fw-bold">{{ 'TICKETS' | translate }}</span>
            <button type="button" class="btn btn-icon btn-sm icon-button" [matTooltip]="'CREATE_NEW_TICKET' | translate" (click)="createNewTicket()">
                <i class="fas fa-plus"></i>
            </button>
        </div>

        <div class="chat-list-items">
            <ngx-spinner [name]="ticketListLoaderId" [fullScreen]="false" type="ball-spin-clockwise" size="medium" bdColor="rgba(51,51,51,0.1)"></ngx-spinner>

            <ng-container *ngFor="let group of ticketGroups">
                <div class="chat-list-group-title">
                    {{ group.label | translate }}
                    <span class="chat-list-group-count">{{ group.tickets.length }}</span>
                </div>
    
                <div class="chat-list-item" *ngFor="let ticket of group.tickets; trackBy: trackByTicket"
                    [class.selected]="ticket.id === selectedTicketId" (click)="selectTicket(ticket.id)">
                    <div class="chat-list-item-body">
                        <div class="d-flex align-items-center">
                            <span class="fw-bold fs-5 text-truncate me-auto">{{ ticket.number }}</span>
                            <span class="chat-list-item-date">{{ ticket.creationDate | date: 'dd/MM/yyyy' }}</span>
                        </div>
                        <div class="d-flex align-items-center">
                            <span class="chat-list-item-subtitle text-truncate me-auto">{{ ticket.type }} - {{ ticket.subtype }}</span>
                            <span class="status-badge" [ngClass]="getStatusClass(ticket.status)">{{ 'INVARIANT_DESCRIPTIONS.' + ticket.status | translate }}</span>
                        </div>
                        <div class="chat-list-item-subtitle text-truncate" *ngIf="ticket.relatedDataOrigin">
                            {{ ticket.relatedDataOrigin.toLocaleUpperCase() | translate }}: {{ ticket.relatedData }}
                        </div>
                    </div>
                </div>
            </ng-container>

            <div class="empty-state" *ngIf="!ticketsLoading && ticketList.length === 0">
                <i class="fas fa-comments fa-2x mb-3"></i>
                <span>{{ 'NO_TICKETS' | translate }}</span>
            </div>
        </div>
    </aside>

    <!-- Conversation -->
    <section class="card chat" *ngIf="selectedTicket as ticket; else noTicketSelected"
        (dragenter)="handleDragEnter($event)" (dragover)="handleDragOver($event)">

        <header class="chat-header">
            <div class="chat-avatar">
                <i class="fas fa-headset"></i>
            </div>
            <div class="flex-grow-1 min-w-0 lh-sm">
                <div class="fw-bold fs-5 text-truncate">{{ ticket.number }}</div>
                <div class="chat-header-subtitle text-truncate">
                    {{ ticket.type }} - {{ ticket.subtype }}
                    <ng-container *ngIf="ticket.relatedDataOrigin">
                        · {{ ticket.relatedDataOrigin.toLocaleUpperCase() | translate }}: {{ ticket.relatedData }}
                    </ng-container>
                </div>
            </div>
            <span class="status-badge" [ngClass]="getStatusClass(ticket.status)">{{ 'INVARIANT_DESCRIPTIONS.' + ticket.status | translate }}</span>
        </header>

        <div class="chat-body">
            <ngx-spinner [name]="messageLoaderId" [fullScreen]="false" type="ball-spin-clockwise" size="medium" bdColor="rgba(51,51,51,0.1)"></ngx-spinner>

            <!-- column-reverse keeps the view anchored to the latest message -->
            <div #messagesScroller class="chat-messages">
                <div class="chat-messages-inner">
                    <ng-container *ngFor="let message of selectedMessages; let i = index; trackBy: trackByMessage">

                        <div class="day-separator" *ngIf="isNewDay(selectedMessages, i)">
                            <span>
                                <ng-container *ngIf="getDayLabelKey(message.creationDate) as dayKey; else fullDate">{{ dayKey | translate }}</ng-container>
                                <ng-template #fullDate>{{ message.creationDate | date: 'dd/MM/yyyy' }}</ng-template>
                            </span>
                        </div>

                        <div class="system-message" *ngIf="message.isSystemMessage; else chatMessage">
                            <span>{{ message.body }}</span>
                        </div>

                        <ng-template #chatMessage>
                            <div class="message-row" [class.own]="isOwnMessage(message)" [class.group-start]="isGroupStart(selectedMessages, i)">

                                <div class="message-avatar" *ngIf="!isOwnMessage(message)">
                                    <img *ngIf="isGroupStart(selectedMessages, i)" [src]="message.picture ?? defaultAvatar" alt="">
                                </div>

                                <div class="bubble" [class.error]="getMessageStatus(message) === MessageStatus.Error">
                                    <div class="bubble-sender" *ngIf="!isOwnMessage(message) && isGroupStart(selectedMessages, i)">
                                        {{ message.createdByName }}
                                    </div>

                                    <a class="bubble-file" *ngFor="let file of message.files" (click)="viewFile(file)">
                                        <i class="fas fa-2x {{ getFileIcon(file.extension) }}"></i>
                                        <span class="text-truncate">{{ file.name }}.{{ file.extension }}</span>
                                    </a>

                                    <span class="bubble-text">{{ message.body }}</span>

                                    <span class="bubble-meta">
                                        {{ message.creationDate | date: 'HH:mm' }}
                                        <ng-container *ngIf="isOwnMessage(message)" [ngSwitch]="getMessageStatus(message)">
                                            <i *ngSwitchCase="MessageStatus.Pending" class="far fa-clock"></i>
                                            <i *ngSwitchCase="MessageStatus.Sent" class="fas fa-check"></i>
                                            <i *ngSwitchCase="MessageStatus.Read" class="fas fa-check-double read"></i>
                                            <i *ngSwitchCase="MessageStatus.Error" class="fas fa-circle-exclamation text-danger" [matTooltip]="'MESSAGE_NOT_SENT' | translate"></i>
                                        </ng-container>
                                    </span>
                                </div>
                            </div>
                        </ng-template>
                    </ng-container>
                </div>
            </div>
        </div>

        <!-- Once shown, the overlay is the only drag target (its children ignore pointer events), so dragleave only fires when the file actually leaves the chat -->
        <div class="drop-overlay" *ngIf="dragOver" (dragleave)="handleDragLeave()" (drop)="handleFileDrop($event)">
            <i class="fas fa-up-to-bracket fa-bounce fa-4x mb-4"></i>
            <span class="fs-4 fw-semibold">{{ 'LOAD_FILES' | translate: { fileNumber: filesBeingHovered } }}</span>
        </div>

        <footer class="chat-footer">
            <div class="pending-files" *ngIf="files.length > 0">
                <div class="pending-file" *ngFor="let file of files">
                    <i class="fas fa-lg {{ getFileIcon(file.name) }}"></i>
                    <div class="min-w-0 lh-sm">
                        <div class="text-truncate fw-semibold">{{ file.name }}</div>
                        <div class="fs-8 text-muted">{{ formatFileSize(file.size) }}</div>
                    </div>
                    <button type="button" class="btn btn-icon btn-sm ms-auto" (click)="removeFile(file)">
                        <i class="fas fa-times"></i>
                    </button>
                </div>
            </div>

            <div class="composer">
                <button type="button" class="btn btn-icon icon-button" [matTooltip]="'ATTACH_FILES' | translate" (click)="fileInput.click()">
                    <i class="fas fa-paperclip"></i>
                </button>
                <input #fileInput type="file" multiple hidden [accept]="supportedFileTypes" (change)="handleFileInput(fileInput)">

                <textarea #messageBox rows="1" class="message-box" [formControl]="messageControl" [placeholder]="'MESSAGE' | translate"
                    (keydown)="handleKeydown($event)" (input)="handleMessageInput()"></textarea>

                <button type="button" class="btn btn-icon send-button" [disabled]="!canSend" [matTooltip]="'SEND' | translate" (click)="sendMessage()">
                    <i class="fas fa-paper-plane-top"></i>
                </button>
            </div>
        </footer>
    </section>

    <ng-template #noTicketSelected>
        <section class="card chat chat-placeholder">
            <div class="empty-state">
                <i class="fas fa-comments fa-3x mb-4"></i>
                <span>{{ 'SELECT_A_TICKET' | translate }}</span>
            </div>
        </section>
    </ng-template>
</div>
